import React, {
  lazy,
  Suspense,
  useEffect,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Switch } from 'react-router-dom';
import 'sweetalert2/src/sweetalert2.scss';
import { Buffer } from 'buffer';
import { receiveCurrentUser } from './actions';
import { AuthRoute, ProtectedRoute } from './components/HOC/RouteUtils';
import Loading from './components/shared/Loading';
import { loadSettings, logout, updateLocalStorageToken } from './utils';

import './App.scss';

import SavedAssets from './components/Installer/SavedAssets/SavedAssets';
import SavedOrders from './components/Installer/SavedOrders/SavedOrders';
import Search from './components/Installer/Search/Search';
import ScanAsset from './components/shared/ScanAsset/ScanAsset';
import Footer from './components/static/Footer/Footer';
import Nav from './components/static/Nav/Nav';
import Pusher from './components/static/Pusher/Pusher';

const { REACT_APP_CURRENT_APP_VERSION } = process.env;

const LoginPage = lazy(() => import('./components/LoginPage/LoginPage').catch(() => window.location.reload()));
const SuperLoginPage = lazy(() => import('./components/LoginPage/SuperLoginPage').catch(() => window.location.reload()));
const PortalSelect = lazy(() => import('./components/PortalSelect/PortalSelect').catch(() => window.location.reload()));
const AccountRecovery = lazy(() => import('./components/AccountRecovery/AccountRecovery').catch(() => window.location.reload()));
const Settings = lazy(() => import('./components/Settings/Settings').catch(() => window.location.reload()));

const AccountInfo = lazy(() => import('./components/RezBiz/AccountInfo/AccountInfo').catch(() => window.location.reload()));
const Services = lazy(() => import('./components/RezBiz/Services/Services').catch(() => window.location.reload()));
const BillingCenter = lazy(() => import('./components/RezBiz/BillingCenter/BillingCenter').catch(() => window.location.reload()));
const Reports = lazy(() => import('./components/RezBiz/Reports/Reports').catch(() => window.location.reload()));

const ContractorHome = lazy(() => import('./components/Contractor/ContractorHome/ContractorHome').catch(() => window.location.reload()));
const WorkOrders = lazy(() => import('./components/Contractor/WorkOrders/WorkOrders').catch(() => window.location.reload()));
const Maps = lazy(() => import('./components/Contractor/Maps/Maps').catch(() => window.location.reload()));
const Calendar = lazy(() => import('./components/Contractor/Calendar/Calendar').catch(() => window.location.reload()));
const RestorationTickets = lazy(() => import('./components/Contractor/RestorationTickets/RestorationTickets').catch(() => window.location.reload()));

const ProgTestLogin = lazy(() => import('./components/ProgrammingTest/ProgTestLogin/ProgTestLogin').catch(() => window.location.reload()));
const ProgrammingTest = lazy(() => import('./components/ProgrammingTest/ProgrammingTest/ProgrammingTest').catch(() => window.location.reload()));

function App() {
  const dispatch = useDispatch();
  const { currentUser } = useSelector((state) => state.session);
  const sessionFromReact = JSON.stringify(currentUser);
  const [portalType, setPortalType] = useState('');

  const {
    nInstaller = 0,
    nContractor = 0,
    UNITYSUPERUSER = 0,
    vcCustomerType = '',
  } = currentUser || {};

  // Hook 1: Handles application boot and token recovery
  useEffect(() => {
    const token = window.token || updateLocalStorageToken();
    if (token) {
      try {
        const bufferObj = Buffer.from(token, 'base64');
        dispatch(receiveCurrentUser(JSON.parse(bufferObj.toString('utf8'))));
      } catch (err) {
        console.error('Failed to parse authentication token:', err);
      }
    }

    document.title = `Portal v${REACT_APP_CURRENT_APP_VERSION}`;
    console.log(`Portal v${REACT_APP_CURRENT_APP_VERSION}`);
    if (document.getElementById('App')) {
      document.getElementById('cannotUpdateCache').classList.add('hidden');
    }
  }, [dispatch]);

  // Hook 2: Handles access management, validation routing, and layout switching
  useEffect(() => {
    if (!currentUser || Object.keys(currentUser).length === 0) {
      return;
    }

    loadSettings(sessionFromReact);

    // Define the list of allowed customer IDs that bypass the transition block
    const allowedCustomerIDs = [
      "0991-6700-0036", "0991-6700-3819", "0991-6700-5115", "0991-6700-8869",
      "0991-6701-5757", "0991-6701-7513", "0991-6702-1543", "0991-6702-4737",
      "0991-6702-7819", "0991-6702-8573", "0991-6703-0399", "0991-6706-8441",
      "0991-6707-5094", "0991-6708-0809", "0991-6710-4461", "0991-6728-0349",
      "0991-6740-3583", "0991-6744-4889", "0991-6744-5991", "0991-6744-8401",
      "0991-6891-4195", "0991-6916-0645", "0991-6948-2094", "0991-6959-6247",
      "0991-6978-0809", "0991-6996-1747", "0991-6999-9777", "0991-7000-5899",
      "0991-7014-9491", "0991-7023-6115", "0991-7026-6349", "0991-7027-2359",
      "0991-7056-8217", "0991-7062-7707", "0991-7063-9185", "0991-7067-3349",
      "0991-7080-2605", "0991-7084-2797", "0991-7098-2901", "0991-7118-4309",
      "0991-7126-0309", "0991-7130-3135", "0991-7155-0675", "0991-7165-2207",
      "0991-7173-7105", "0991-7183-8195", "0991-7191-7625", "0991-7201-8839",
      "0991-7226-4034", "0991-7265-3309", "0991-7272-4054", "0991-7405-0004",
      "0991-7471-8839", "0991-7497-6543", "0991-7532-2869", "0991-7590-8421",
      "0991-7668-5411", "0991-7695-0787", "0991-7754-4329", "0991-7756-0074",
      "0991-7863-0971"
    ];

  const isWholesale = vcCustomerType === 'Wholesale';
  const isInstallerOrContractor = nInstaller > 0 || nContractor > 0;
  const isSuperUser = UNITYSUPERUSER === 1;
  const isAllowedBusinessID = allowedCustomerIDs.includes(currentUser.vcCustomerID);

  // Automatically block Consumer types from frontend processing
  if (vcCustomerType === 'Consumer') {
    console.warn('Consumer accounts are not permitted. Logging out.');
    dispatch(logout());
    return;
  }

  // Enforce structural bypass: allow wholesale, installers/contractors, superusers, or listed business IDs
  if (!isWholesale && !isInstallerOrContractor && !isSuperUser && !isAllowedBusinessID) {
    console.warn('Unauthorized user classification detected. Logging out user.');
      dispatch(logout());
      return;
    }

    if (isSuperUser || !isInstallerOrContractor) {
      setPortalType('rezBiz');
    }
  }, [currentUser, sessionFromReact, vcCustomerType, nInstaller, nContractor, UNITYSUPERUSER, dispatch]);

  return (
    <div id="App" className="App">
      <Nav portalType={portalType} />
      <Pusher />
      <Suspense fallback={<Loading />}>
        <Switch>
          <AuthRoute path="/login/superUser/:vcCustomerID/:hash/:nEmployeeID" component={SuperLoginPage} />
          <AuthRoute path="/login" component={LoginPage} />
          <Route path="/accountRecovery" component={AccountRecovery} />
          <ProtectedRoute path="/portalSelect" component={PortalSelect} portalType={portalType} setPortalType={setPortalType} />
          <ProtectedRoute path="/settings" component={Settings} setPortalType={setPortalType} />

          {/* Rez, Biz and wholesale sites */}
          <ProtectedRoute path="/accountInfo" component={AccountInfo} />
          <ProtectedRoute path="/services" component={Services} />
          <ProtectedRoute path="/billingCenter" component={BillingCenter} />
          <ProtectedRoute path="/reports" component={Reports} />

          {/* contractor sites */}
          <ProtectedRoute path="/contractorHome" component={ContractorHome} />
          <ProtectedRoute path="/workOrders" component={WorkOrders} />
          <ProtectedRoute path="/calendar" component={Calendar} />
          <ProtectedRoute path="/restorationTickets" component={RestorationTickets} />
          <ProtectedRoute path="/scanAsset" component={ScanAsset} />
          <ProtectedRoute path="/maps/:params" component={Maps} />
          <ProtectedRoute path="/maps" component={Maps} />

          {/* installer sites */}
          <ProtectedRoute path="/search" component={Search} />
          <ProtectedRoute path="/savedWorkOrders" component={SavedOrders} />
          <ProtectedRoute path="/scanAsset" component={ScanAsset} />
          <ProtectedRoute path="/SavedAssets" component={SavedAssets} />

          {/* Programming test */}
          <Route path="/programmingTest" component={ProgTestLogin} />
          <Route path="/programmingTestPage" component={ProgrammingTest} />
          <Route path="/" component={LoginPage} />
        </Switch>
      </Suspense>
      <Footer />
    </div>
  );
}

export default App;